import { StrictMode, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import '@fontsource-variable/manrope';
import './styles/tokens.css';
import './styles/app.css';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { ToastProvider } from './components/Toast';
import { CompanyContext } from './pages/CompanyContext';
import { NewCompany } from './pages/NewCompany';
import { Signup } from './pages/Signup';
import { Login } from './pages/Login';
import { AcceptInvitation } from './pages/AcceptInvitation';
import { ForgotPassword, ResetPassword } from './pages/PasswordRecovery';
import { NotFound } from './pages/NotFound';
import { Overview } from './pages/Overview';
import { MarketAnalysis } from './pages/MarketAnalysis';
import { MarketList } from './pages/MarketList';
import { MarketNew } from './pages/MarketNew';
import { TargetDetail, Targets } from './pages/Targets';
import { People } from './pages/People';
import { Discovery } from './pages/Discovery';
import { CommercialMap } from './pages/CommercialMap';
import { Monitoring } from './pages/Monitoring';
import { Schedules } from './pages/Schedules';
import { Settings } from './pages/Settings';
import { Integrations } from './pages/Integrations';
import { Signals } from './pages/Signals';
import { Sources } from './pages/Sources';
import { Battlecards } from './pages/Battlecards';
import { BattlecardDetail } from './pages/BattlecardDetail';
import { Segmentation } from './pages/Segmentation';
import { SegmentationDetail } from './pages/SegmentationDetail';
import { Sizing } from './pages/Sizing';
import { SizingDetail } from './pages/SizingDetail';
import { Environment } from './pages/Environment';
import { EnvironmentDetail } from './pages/EnvironmentDetail';
import { Portfolio } from './pages/Portfolio';
import { PortfolioDetail } from './pages/PortfolioDetail';
import { ExecutiveReport } from './pages/ExecutiveReport';
import { DecisionDetail, Decisions } from './pages/Decisions';
import { ScenarioSetPage, Scenarios } from './pages/Scenarios';
import { Learning } from './pages/Learning';
// IM-JRN-007: jornadas (só aparecem com a camada ligada; desligada, as rotas mostram "Página não encontrada").
import { Journeys } from './pages/Journeys';
import { JourneyDetail } from './pages/JourneyDetail';
// IM-XPR-001..004 (IM-ADR-015): experiência orientada a objetivos, só com EXPERIENCE_V2_ENABLED=true. Desligada,
// as rotas novas mostram "Página não encontrada" e tudo o mais continua como antes.
import { ExperienceProvider } from './experience/ExperienceContext';
import { HomeSwitch, ShellSwitch, V2Only } from './experience/switches';
import { Objectives } from './experience/Objectives';
import { ObjectiveDetail } from './experience/ObjectiveDetail';
import { TargetDossierRoute, TargetsSection } from './experience/TargetsJourney';
import { Follow } from './experience/Follow';
import { PlanData } from './experience/PlanData';
import { PlanConnectors } from './experience/PlanConnectors';
import { AnalysesHub } from './experience/AnalysesHub';
// IM-UX-163 (IM-ADR-019): administração da plataforma, contexto separado do produto.
import { PLATFORM_BASE, PlatformRoot } from './platform/PlatformAdmin';
import { OrganizationSuspended } from './pages/OrganizationSuspended';
// IM-UX-176..183 (IM-SAAS-003): administração da empresa pelo próprio tenant.
import { OrganizationAdmin } from './pages/OrganizationAdmin';
import { ChooseOrganization } from './pages/ChooseOrganization';
import { PublicPlans } from './pages/PublicPlans';
import { CommercialResume, CommercialSignup, ConfirmCommercialEmail } from './pages/CommercialOnboarding';
import './styles/experience.css';
import './styles/xpr005.css';
import './styles/xpr006.css';
import './styles/planning.css';
import './styles/saas003.css';
import './styles/saas007.css';
import { isDedicatedAppHost } from './lib/surface';

function RequireAuth({ children }: { children: ReactNode }) {
  const { status, me } = useAuth();
  const location = useLocation();
  if (status === 'loading') return <div className="sr-only" role="status">Carregando sessão…</div>;
  if (status === 'anonymous') return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  // IM-UX-163: sessão na administração da plataforma não abre o produto; conta administrativa dedicada não tem empresa.
  if (me?.context === 'PLATFORM' || (!me?.activeOrganization && me?.platform?.accountKind === 'PLATFORM')) return <Navigate to={PLATFORM_BASE} replace />;
  // IM-SAAS-003: acesso à organização ativa encerrado (desativado ou removido), mas a pessoa tem outras — escolher.
  if (!me?.activeOrganization && me?.organizations.length) return <ChooseOrganization />;
  // IM-CTX-REQ-002: sem empresa ativa, o primeiro passo é criar a empresa (onboarding).
  if (!me?.activeOrganization) return <Navigate to="/empresa/nova" replace />;
  // IM-ADR-020: organização suspensa — o servidor recusa tudo; a tela explica e oferece as outras organizações.
  if (me.activeOrganization.suspended) return <OrganizationSuspended />;
  if (me.activeOrganization.commercialStatus === 'PENDENTE' && !location.pathname.startsWith('/contratar/')) return <Navigate to="/contratar/retomar" replace />;
  return <>{children}</>;
}

function RequireSession({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();
  if (status === 'loading') return <div className="sr-only" role="status">Carregando sessão…</div>;
  if (status === 'anonymous') return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  return <>{children}</>;
}
function NewCompanyRoute() {
  const { me } = useAuth();
  return me?.organizations.length ? <Navigate to="/empresas/nova" replace /> : <NewCompany />;
}

const TITLES: Record<string, string> = {
  '/': 'Visão geral', '/visao-geral': 'Visão geral', '/objetivos': 'Meus objetivos', '/acompanhar': 'Acompanhar', '/analises': 'Análises e dados', '/sinais': 'Sinais de mercado', '/oportunidades': 'Oportunidades', '/contas': 'Contas prioritárias',
  '/movimentos': 'Próximos movimentos', '/fontes': 'Fontes', '/mercado': 'Inteligência de Mercado', '/empresas-alvo': 'Empresas-alvo', '/pessoas': 'Pessoas e decisores', '/monitoramento': 'Monitoramentos', '/agendas': 'Agendas e entregas', '/mercado/nova': 'Nova análise de mercado', '/battlecards': 'Battlecards', '/segmentacao': 'Segmentação e atratividade', '/tam-sam-som': 'TAM, SAM e SOM', '/pestel-porter': 'PESTEL e Cinco Forças', '/portfolio': 'Portfólio e Ecossistema', '/parecer': 'Parecer executivo', '/decisoes': 'Central de decisões', '/cenarios': 'Cenários e simulações', '/aprendizado': 'Resultados e aprendizado', '/configuracoes': 'Configurações', '/configuracoes/integracoes': 'Integrações', '/login': 'Acesso', '/cadastro': 'Criar conta', '/recuperar-acesso': 'Recuperar acesso', '/redefinir-senha': 'Definir nova senha', '/empresa/nova': 'Nova empresa', '/objetivos/empresas/carteira': 'Minhas empresas', '/objetivos/empresas/analisar': 'Analisar uma empresa', '/objetivos/empresas/descobrir': 'Descobrir novas empresas', '/objetivos/planejar/dados': 'Base financeira', '/objetivos/acompanhar-plano/integracoes': 'Integrações do plano', '/empresas/nova': 'Nova empresa',
};
function TitleSync() {
  const { pathname } = useLocation();
  document.title = `${TITLES[pathname] ?? (pathname.startsWith('/configuracoes/contexto') ? 'Contexto da empresa' : pathname.startsWith('/configuracoes/administracao') ? 'Administração da empresa' : pathname.startsWith('/mercado/') ? 'Análise de mercado' : pathname.startsWith('/battlecards/') ? 'Battlecard' : pathname.startsWith('/segmentacao/') ? 'Análise de segmentação' : pathname.startsWith('/tam-sam-som/') ? 'Estimativa de TAM, SAM e SOM' : pathname.startsWith('/pestel-porter/') ? 'Análise PESTEL e Cinco Forças' : pathname.startsWith('/portfolio/') ? 'Análise de portfólio' : pathname.startsWith('/decisoes/') ? 'Decisão' : pathname.startsWith('/cenarios/') ? 'Simulação de cenários' : pathname.startsWith('/objetivos/empresas/empresa/') ? 'Dossiê da empresa' : pathname.startsWith('/convite/') ? 'Convite' : pathname.startsWith(PLATFORM_BASE) ? 'Administração da plataforma' : pathname.startsWith('/objetivos/') ? 'Objetivo' : 'InteliMove')} · InteliMove`;
  return null;
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BrowserRouter>
      <AuthProvider>
        <ExperienceProvider>
        <ToastProvider>
          <TitleSync />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/planos" element={<PublicPlans />} />
            <Route path="/contratar/cadastro" element={<CommercialSignup />} />
            <Route path="/confirmar-email" element={<ConfirmCommercialEmail />} />
            <Route path="/contratar/retomar" element={<RequireSession><CommercialResume /></RequireSession>} />
            <Route path="/cadastro" element={<Signup />} />
            <Route path="/convite/:token" element={<AcceptInvitation />} />
            <Route path="/recuperar-acesso" element={<ForgotPassword />} />
            <Route path="/redefinir-senha" element={<ResetPassword />} />
            <Route path="/empresa/nova" element={<RequireSession><NewCompanyRoute /></RequireSession>} />
            {!isDedicatedAppHost() && <Route path={`${PLATFORM_BASE}/*`} element={<RequireSession><PlatformRoot /></RequireSession>} />}
            <Route element={<RequireAuth><ShellSwitch /></RequireAuth>}>
              <Route index element={<HomeSwitch />} />
              {/* IM-UX-109: a visão geral anterior continua inteira, também na nova experiência (Análises e dados). */}
              <Route path="visao-geral" element={<Overview />} />
              <Route path="objetivos" element={<V2Only><Objectives /></V2Only>} />
              <Route path="objetivos/:key" element={<V2Only><ObjectiveDetail /></V2Only>} />
              {/* IM-XPR-006: carteira, inclusão, descoberta e dossiê DENTRO do objetivo (mesma trilha e cabeçalho). */}
              <Route path="objetivos/empresas/empresa/:id" element={<V2Only><TargetDossierRoute /></V2Only>} />
              <Route path="objetivos/empresas/:secao" element={<V2Only><TargetsSection /></V2Only>} />
              {/* IM-JRN-013/014/017: base financeira e integrações DENTRO dos objetivos (sem item novo no menu). */}
              <Route path="objetivos/planejar/dados" element={<V2Only><PlanData /></V2Only>} />
              <Route path="objetivos/acompanhar-plano/integracoes" element={<V2Only><PlanConnectors /></V2Only>} />
              <Route path="acompanhar" element={<V2Only><Follow /></V2Only>} />
              <Route path="analises" element={<V2Only><AnalysesHub /></V2Only>} />
              <Route path="sinais" element={<Signals />} />
              {/* IM-EXT-003: telas demonstrativas substituídas por dados reais; oportunidade/pipeline é função de CRM (fica no Ceres). */}
              <Route path="oportunidades" element={<Navigate to="/empresas-alvo" replace />} />
              <Route path="contas" element={<Navigate to="/empresas-alvo" replace />} />
              <Route path="movimentos" element={<Navigate to="/" replace />} />
              <Route path="empresas-alvo" element={<Targets />} />
              {/* IM-EXT-013: "Descobrir por dados abertos" virou estratégia interna de "Encontrar empresas" (link antigo redireciona). */}
              <Route path="empresas-alvo/descoberta" element={<Navigate to="/empresas-alvo?encontrar=1" replace />} />
              <Route path="empresas-alvo/descobertas-anteriores" element={<Discovery />} />
              <Route path="empresas-alvo/:id" element={<TargetDetail />} />
              <Route path="mapa-comercial" element={<CommercialMap />} />
              <Route path="pessoas" element={<People />} />
              <Route path="monitoramento" element={<Monitoring />} />
              <Route path="agendas" element={<Schedules />} />
              <Route path="fontes" element={<Sources />} />
              <Route path="battlecards" element={<Battlecards />} />
              <Route path="battlecards/:id" element={<BattlecardDetail />} />
              <Route path="segmentacao" element={<Segmentation />} />
              <Route path="segmentacao/:id" element={<SegmentationDetail />} />
              <Route path="tam-sam-som" element={<Sizing />} />
              <Route path="tam-sam-som/:id" element={<SizingDetail />} />
              <Route path="pestel-porter" element={<Environment />} />
              <Route path="pestel-porter/:id" element={<EnvironmentDetail />} />
              <Route path="portfolio" element={<Portfolio />} />
              <Route path="portfolio/:id" element={<PortfolioDetail />} />
              <Route path="parecer" element={<ExecutiveReport />} />
              <Route path="decisoes" element={<Decisions />} />
              <Route path="decisoes/:id" element={<DecisionDetail />} />
              <Route path="cenarios" element={<Scenarios />} />
              <Route path="cenarios/:id" element={<ScenarioSetPage />} />
              <Route path="aprendizado" element={<Learning />} />
              <Route path="jornadas" element={<Journeys />} />
              <Route path="jornadas/:id" element={<JourneyDetail />} />
              <Route path="mercado" element={<MarketList />} />
              <Route path="mercado/nova" element={<MarketNew />} />
              <Route path="mercado/:id" element={<MarketAnalysis />} />
              <Route path="configuracoes" element={<Settings />} />
              <Route path="configuracoes/integracoes" element={<Integrations />} />
              <Route path="configuracoes/administracao" element={<OrganizationAdmin />} />
              <Route path="configuracoes/administracao/:secao" element={<OrganizationAdmin />} />
              <Route path="configuracoes/contexto" element={<CompanyContext />} />
              <Route path="configuracoes/contexto/:secao" element={<CompanyContext />} />
              <Route path="empresas/nova" element={<NewCompany />} />
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </ToastProvider>
        </ExperienceProvider>
      </AuthProvider>
    </BrowserRouter>
  </StrictMode>,
);
